import { useState, useEffect, useMemo } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { getJson, getJsonUnwrapped } from "@/lib/http";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Calendar, CalendarDays, Plus, Clock, CheckCircle, XCircle, AlertCircle, Target, Edit, Trash2, Megaphone, Users, TrendingUp, AlertTriangle, Download, Upload, Trash } from "lucide-react";
import { LoadingSpinner, PageLoadingSpinner, InlineLoadingSpinner } from "@/components/loading-spinner";
import { getRetailersByCampaignId } from "@/api/retailers";
import { dbg } from "@/lib/dbg";
import { LoadingLink } from "@/components/loading-link";
import { useToast } from "@/hooks/use-toast";
import { format } from "date-fns";
import { Link } from "wouter";
import type { BookingCycle, Retailer, Store, MediaFormat, Campaign } from "@shared/schema";

type CampaignStatus = {
  id: number;
  name: string;
  color?: string | null;
};

interface AvailabilityData {
  storeId: number;
  storeName: string;
  formatId: number;
  formatName: string;
  totalCapacity: number;
  bookedCapacity: number;
  availableCapacity: number;
  status: "available" | "limited" | "full";
}

// Helper functions for campaign status display
const getStatusDisplayName = (status: string) => {
  const statusMap: Record<string, string> = {
    'draft': 'Draft',
    'confirmed': 'Confirmed',
    'artwork_received': 'Artwork Received',
    'artwork_approved': 'Artwork Approved',
    'printed': 'Printed',
    'posted': 'Posted',
    'removed': 'Removed',
    // Legacy status mappings for backward compatibility
    'optioned': 'Draft',
    'booked': 'Confirmed',
    'live': 'Posted',
    'completed': 'Removed'
  };
  return statusMap[status] || status.charAt(0).toUpperCase() + status.slice(1);
};

const getStatusVariant = (status: string): "default" | "secondary" | "destructive" | "outline" => {
  switch (status) {
    case 'draft':
    case 'optioned':
      return 'outline';
    case 'confirmed':
    case 'booked':
      return 'secondary';
    case 'artwork_received':
    case 'artwork_approved':
      return 'secondary';
    case 'printed':
      return 'default';
    case 'posted':
    case 'live':
      return 'default';
    case 'removed':
    case 'completed':
      return 'destructive';
    default:
      return 'secondary';
  }
};

// Compute retailer for a campaign using already-loaded cycles and retailers
const CampaignRetailer = ({ cycleId, retailers, cycles, retailerIdFromCampaign }: { cycleId?: number | null; retailers: Retailer[]; cycles: BookingCycle[]; retailerIdFromCampaign?: number | null }) => {
  const cycle = cycleId ? cycles.find(c => c.id === cycleId) : undefined;
  const retailerId = cycle?.retailerId ?? retailerIdFromCampaign ?? null;
  const retailer = retailerId ? retailers.find(r => r.id === retailerId) : undefined;
  return <span className="text-muted-foreground">{retailer?.name || '—'}</span>;
};



// Component to fetch and display booking cycle name for a campaign
const CampaignCycle = ({ cycleId }: { cycleId?: number }) => {
  const { data: cycles = [] } = useQuery<BookingCycle[]>({
    queryKey: ["/api/booking-cycles"],
    queryFn: async () => {
      console.log('[QUERY_DEBUG] Fetching /api/booking-cycles');
      return getJson<BookingCycle[]>('/api/booking-cycles');
    }
  });

  if (!cycleId) {
    return <span className="text-muted-foreground">No cycle assigned</span>;
  }

  const cycle = cycles.find(c => c.id === cycleId);
  return <span>{cycle?.name || `Cycle ${cycleId}`}</span>;
};

export default function BookingEngine() {
  const { toast } = useToast();
  const [selectedRetailer, setSelectedRetailer] = useState<number | null>(null);
  const [selectedCycle, setSelectedCycle] = useState<BookingCycle | null>(null);

  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);
  const [selectedCell, setSelectedCell] = useState<{store: Store; format: MediaFormat} | null>(null);
  const [isEditCampaignOpen, setIsEditCampaignOpen] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState<Campaign | null>(null);
  const [isEditBookingOpen, setIsEditBookingOpen] = useState(false);
  const [editingBooking, setEditingBooking] = useState<any | null>(null);
  const [selectedStores, setSelectedStores] = useState<number[]>([]);
  const [selectedMediaFormats, setSelectedMediaFormats] = useState<{formatId: number; quantity: number}[]>([]);
  
  // Filter states
  const [filterRetailer, setFilterRetailer] = useState<number | null>(null);
  const [filterCycle, setFilterCycle] = useState<number | null>(null);
  const [filterStatus, setFilterStatus] = useState<string>("");

  // Handle URL parameters for initial filtering
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const cycleId = urlParams.get('cycleId');
    
    if (cycleId) {
      const cycleIdNum = parseInt(cycleId, 10);
      if (!isNaN(cycleIdNum)) {
        setFilterCycle(cycleIdNum);
      }
    }
  }, []);


  // Fetch retailers
  const { data: retailers = [] } = useQuery<Retailer[]>({
    queryKey: ["/api/retailers"],
    queryFn: async () => {
      console.log('[QUERY_DEBUG] Fetching /api/retailers');
      return getJson('/api/retailers');
    }
  });

  // Fetch all booking cycles (always enabled for edit dialog)
  const { data: allCycles = [] } = useQuery<BookingCycle[]>({
    queryKey: ["/api/booking-cycles"],
    queryFn: async () => {
      console.log('[QUERY_DEBUG] Fetching /api/booking-cycles');
      return getJson('/api/booking-cycles');
    },
  });

  // Filter cycles by selected retailer
  const cycles = selectedRetailer 
    ? allCycles.filter((cycle: BookingCycle) => cycle.retailerId === selectedRetailer)
    : [];

  // Fetch stores and formats for availability grid
  const { data: stores = [] } = useQuery<Store[]>({
    queryKey: ["/api/stores"],
    queryFn: async () => {
      console.log('[QUERY_DEBUG] Fetching /api/stores');
      return getJson('/api/stores');
    }
  });

  const { data: storesByRetailer = [] } = useQuery<Store[]>({
    queryKey: ["/api/retailers", selectedRetailer, "stores"],
    queryFn: async () => {
      console.log('[QUERY_DEBUG] Fetching /api/retailers/' + selectedRetailer + '/stores');
      return getJson(`/api/retailers/${selectedRetailer}/stores`);
    },
    enabled: !!selectedRetailer,
  });

  // [PHASE2_PAGINATION] Using getJsonUnwrapped for backward compatibility
  const { data: mediaFormats = [] } = useQuery<MediaFormat[]>({
    queryKey: ["/api/media-formats"],
    queryFn: async () => {
      console.log('[QUERY_DEBUG] Fetching /api/media-formats');
      return getJsonUnwrapped<MediaFormat>('/api/media-formats');
    }
  });

  // Fetch availability data for selected cycle
  const { data: availabilityGrid } = useQuery({
    queryKey: ["/api/availability-grid", selectedCycle?.id],
    queryFn: async () => {
      console.log('[QUERY_DEBUG] Fetching /api/availability-grid/' + selectedCycle?.id);
      return getJson<AvailabilityData[]>(`/api/availability-grid/${selectedCycle?.id}`);
    },
    enabled: !!selectedCycle,
  });

  // Fetch campaigns
  const { data: campaignsResponse, refetch: refetchCampaigns } = useQuery<{ data: Campaign[]; totalCount: number } | Campaign[]>({
    queryKey: ["/api/campaigns"],
    queryFn: async () => {
      console.log('[QUERY_DEBUG] Fetching /api/campaigns');
      return getJson<{ data: Campaign[]; totalCount: number } | Campaign[]>('/api/campaigns');
    },
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });
  // Handle both paginated response { data: [...] } and legacy array response
  const campaigns = useMemo(() => {
    if (!campaignsResponse) return [] as Campaign[];
    if (Array.isArray(campaignsResponse)) return campaignsResponse;
    if (campaignsResponse.data && Array.isArray(campaignsResponse.data)) return campaignsResponse.data;
    return [] as Campaign[];
  }, [campaignsResponse]);

  // Fetch campaign statuses
  const { data: campaignStatuses = [] } = useQuery<CampaignStatus[]>({
    queryKey: ["/api/campaign-statuses"],
    queryFn: async () => {
      console.log('[QUERY_DEBUG] Fetching /api/campaign-statuses');
      return getJson<CampaignStatus[]>('/api/campaign-statuses');
    }
  });

  // Fetch admin dashboard analytics
  const { data: campaignStats } = useQuery<{
    totalCampaigns: number;
    draftCampaigns: number;
    confirmedCampaigns: number;
    artworkReceivedCampaigns: number;
    artworkApprovedCampaigns: number;
    printedCampaigns: number;
    postedCampaigns: number;
    postingConfirmedCampaigns: number;
    removedCampaigns: number;
    totalOperators: number;
    activeOperators: number;
    overallCompletionRate: number;
    totalIssues: number;
  }>({
    queryKey: ["/api/analytics/campaign-stats"],
    queryFn: async () => {
      console.log('[QUERY_DEBUG] Fetching /api/analytics/campaign-stats');
      return getJson('/api/analytics/campaign-stats');
    }
  });

  // [PHASE2_PAGINATION] Using getJsonUnwrapped for backward compatibility
  const { data: operators = [] } = useQuery({
    queryKey: ["/api/users/operators"],
    queryFn: async () => {
      console.log('[QUERY_DEBUG] Fetching /api/users/operators');
      return getJsonUnwrapped('/api/users/operators');
    }
  });

  // Get cycles for the selected retailer
  const retailerCycles = filterRetailer 
    ? allCycles.filter((cycle: BookingCycle) => cycle.retailerId === filterRetailer)
    : allCycles;

  // Filter campaigns by retailer, cycle, and status
  const filteredCampaigns = campaigns.filter((campaign) => {
    // Filter by retailer (through cycle relationship)
    if (filterRetailer) {
      const campaignCycle = allCycles.find(c => c.id === campaign.cycleId);
      if (!campaignCycle || campaignCycle.retailerId !== filterRetailer) return false;
    }
    if (filterCycle && campaign.cycleId !== filterCycle) return false;
    if (filterStatus && filterStatus !== "all") {
      // Use statusId for filtering when available, fall back to legacy status
      if (campaign.statusId) {
        if (campaign.statusId.toString() !== filterStatus) return false;
      } else if (campaign.status !== filterStatus) return false;
    }
    return true;
  });

  // Check if filters are hiding campaigns and show notification
  useEffect(() => {
    if (campaigns.length > 0) {
      const hasFilters = filterRetailer || filterCycle || filterStatus;
      const hiddenCampaigns = campaigns.length - filteredCampaigns.length;
      
      if (hasFilters && hiddenCampaigns > 0) {
        dbg.log(`Filters active: ${hiddenCampaigns} campaigns hidden`);
      }
      dbg.log("Total campaigns loaded:", campaigns.length);
      dbg.log("Filtered campaigns:", filteredCampaigns.length);
      dbg.log("Current filters:", { filterRetailer, filterCycle, filterStatus });
      const newCampaigns = campaigns.filter(c => c.id > 110);
      if (newCampaigns.length > 0) {
        dbg.log("New campaigns (ID > 110):", newCampaigns);
      }
      const campaignsWithoutCycles = campaigns.filter(c => !c.cycleId);
      if (campaignsWithoutCycles.length > 0) {
        dbg.log("Campaigns without cycles:", campaignsWithoutCycles.length);
      }
    }
  }, [campaigns, filteredCampaigns, filterRetailer, filterCycle, filterStatus]);

  // Reset cycle filter when retailer changes
  const handleRetailerChange = (retailerId: number | null) => {
    setFilterRetailer(retailerId);
    setFilterCycle(null); // Reset cycle when retailer changes
  };

  const handleEditCampaign = (campaign: Campaign) => {
    setEditingCampaign(campaign);
    setIsEditCampaignOpen(true);
  };

  const handleEditBooking = async (campaign: Campaign) => {
    try {
      // Fetch campaign details including stores, media, and retailers
      const [storesResponse, mediaResponse, retailersResponse] = await Promise.all([
        fetch(`/api/campaigns/${campaign.id}/stores`).then(res => res.json()),
        fetch(`/api/campaign-snapshots/${campaign.id}/media-summary`).then(res => res.json()),
        getRetailersByCampaignId(campaign.id)
      ]);
      
      // Extract store IDs and media format configurations
      const storeIds = storesResponse.map((item: any) => item.store?.id).filter(Boolean);
      const mediaFormats = mediaResponse.map((item: any) => ({
        formatId: item.mediaFormat.id,
        quantity: item.quantity
      }));
      
      // Get the first retailer associated with this campaign
      const campaignRetailer = retailersResponse[0];
      if (campaignRetailer) {
        setSelectedRetailer(campaignRetailer.id);
      }
      
      setEditingBooking(campaign);
      setSelectedStores(storeIds);
      setSelectedMediaFormats(mediaFormats);
      setIsEditBookingOpen(true);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to fetch campaign details",
        variant: "destructive",
      });
    }
  };

  // Removal campaign functionality removed - campaigns are now created via the mirror-creation panel

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Campaign Management</h1>
          <p className="text-muted-foreground">Monitor field operations, campaigns, and booking cycles</p>
        </div>
        <div className="flex items-center space-x-3">

          <LoadingLink href="/create-campaign">
            <Target className="h-4 w-4 mr-2" />
            Create Campaign
          </LoadingLink>
        </div>
      </div>

      {/* Admin Statistics */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Campaign Overview</p>
              <div className="mt-2 grid grid-cols-2 gap-4">
                <div>
                  <p className="text-lg font-bold text-gray-800">{campaignStats?.confirmedCampaigns || 0}</p>
                  <p className="text-xs text-muted-foreground">Confirmed</p>
                </div>
                <div>
                  <p className="text-lg font-bold text-gray-800">{campaignStats?.draftCampaigns || 0}</p>
                  <p className="text-xs text-muted-foreground">Draft</p>
                </div>
                <div>
                  <p className="text-lg font-bold text-gray-800">{campaignStats?.postedCampaigns || 0}</p>
                  <p className="text-xs text-muted-foreground">Posted</p>
                </div>
                <div>
                  <p className="text-lg font-bold text-gray-800">{campaignStats?.artworkReceivedCampaigns || 0}</p>
                  <p className="text-xs text-muted-foreground">Artwork</p>
                </div>
              </div>
            </div>
            <div className="p-3 bg-primary-50 rounded-full">
              <Megaphone className="h-6 w-6 text-primary-500" />
            </div>
          </div>
          <div className="mt-4 flex items-center">
            <span className="text-success-600 text-sm font-medium">
              {campaignStats?.totalCampaigns || campaigns.length} total campaigns
            </span>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Field Operators</p>
              <p className="text-3xl font-bold text-gray-800 mt-2">
                {campaignStats?.totalOperators || (operators as any[]).length}
              </p>
            </div>
            <div className="p-3 bg-success-50 rounded-full">
              <Users className="h-6 w-6 text-success-500" />
            </div>
          </div>
          <div className="mt-4 flex items-center">
            <span className="text-success-600 text-sm font-medium">
              {campaignStats?.activeOperators || Math.floor((operators as any[]).length * 0.75)} active now
            </span>
          </div>
        </Card>


      </div>

      {/* Campaign Bookings Table */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Campaign Bookings</CardTitle>
              <CardDescription>
                Manage and monitor all campaign bookings
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="text-xs">
                {filteredCampaigns.length} campaigns
              </Badge>
              {(filterRetailer || filterCycle || filterStatus) && (
                <Badge variant="secondary" className="text-xs">
                  {campaigns.length - filteredCampaigns.length} hidden by filters
                </Badge>
              )}
            </div>
          </div>
          
          {/* Filter Controls */}
          <div className="flex flex-wrap gap-4 mt-4 p-4 bg-muted/50 rounded-lg">
            <div className="flex items-center gap-2">
              <Label htmlFor="filter-retailer" className="text-sm font-medium">Retailer:</Label>
              <Select value={filterRetailer?.toString() || "all"} onValueChange={(value) => handleRetailerChange(value === "all" ? null : parseInt(value))}>
                <SelectTrigger className="w-48">
                  <SelectValue placeholder="All Retailers" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Retailers</SelectItem>
                  {(retailers || []).map((retailer) => (
                    <SelectItem key={retailer.id} value={retailer.id.toString()}>
                      {retailer.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center gap-2">
              <Label htmlFor="filter-cycle" className="text-sm font-medium">Cycle:</Label>
              <Select 
                value={filterCycle?.toString() || "all"} 
                onValueChange={(value) => setFilterCycle(value === "all" ? null : parseInt(value))}
                disabled={!filterRetailer}
              >
                <SelectTrigger className="w-48">
                  <SelectValue placeholder={filterRetailer ? "All Cycles" : "Select Retailer First"} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Cycles</SelectItem>
                  {(retailerCycles || []).map((cycle) => (
                    <SelectItem key={cycle.id} value={cycle.id.toString()}>
                      {cycle.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center gap-2">
              <Label htmlFor="filter-status" className="text-sm font-medium">Status:</Label>
              <Select value={filterStatus || "all"} onValueChange={(value) => setFilterStatus(value === "all" ? "" : value)}>
                <SelectTrigger className="w-40">
                  <SelectValue placeholder="All Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  {(campaignStatuses || []).map((status) => (
                    <SelectItem key={status.id} value={status.id.toString()}>
                      <div className="flex items-center gap-2">
                        <div 
                          className="w-3 h-3 rounded-full" 
                          style={{ backgroundColor: status.color || '#6b7280' }}
                        />
                        {status.name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>



            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => {
                setFilterRetailer(null);
                setFilterCycle(null);
                setFilterStatus("");
              }}
            >
              Clear Filters
            </Button>

            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => {
                queryClient.invalidateQueries({ queryKey: ['/api/campaigns'] });
                queryClient.invalidateQueries({ queryKey: ['/api/analytics/campaign-stats'] });
                toast({
                  title: "Data Refreshed",
                  description: "Campaign data has been updated",
                });
              }}
            >
              Refresh Data
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Campaign Name</TableHead>
                <TableHead>Retailer</TableHead>
                <TableHead>Campaign Type</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Booking Cycle</TableHead>
                <TableHead>Created</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredCampaigns.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                    No campaigns found. Adjust your filters or create a new campaign.
                  </TableCell>
                </TableRow>
              ) : (
                filteredCampaigns.map((campaign) => (
                  <TableRow key={campaign.id}>
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        <Link href={`/campaigns/${campaign.id}`} className="text-primary hover:underline cursor-pointer">
                          {campaign.campaignTypeKey === 'Removal' ? (
                            <div>
                              <div className="font-medium text-red-700">Removal</div>
                              <div className="text-xs text-muted-foreground">
                                of&nbsp;{(campaign as any).parentName ?? 'Unknown'}
                              </div>
                            </div>
                          ) : (
                            campaign.name
                          )}
                        </Link>
                        {(campaign as any).pendingCreation && (
                          <Badge variant="secondary" className="bg-gray-100 text-gray-600 text-xs">
                            Pending
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <CampaignRetailer cycleId={campaign.cycleId} retailerIdFromCampaign={(campaign as any).retailerId} cycles={allCycles} retailers={retailers} />
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-muted-foreground">{campaign.campaignTypeKey || 'N/A'}</span>
                    </TableCell>
                    <TableCell>
                      {campaign.statusId ? (
                        (() => {
                          const status = campaignStatuses.find(s => s.id === campaign.statusId);
                          return status ? (
                            <Badge variant="secondary" className="flex items-center gap-2">
                              <div 
                                className="w-2 h-2 rounded-full" 
                                style={{ backgroundColor: status.color ?? '#9CA3AF' }}
                              />
                              {status.name}
                            </Badge>
                          ) : (
                            <Badge variant={getStatusVariant(campaign.status)}>
                              {getStatusDisplayName(campaign.status)}
                            </Badge>
                          );
                        })()
                      ) : (
                        <Badge variant={getStatusVariant(campaign.status)}>
                          {getStatusDisplayName(campaign.status)}
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <CampaignCycle cycleId={campaign.cycleId || undefined} />
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col">
                        <span className="font-medium text-sm">
                          {(campaign as any).creatorName || 'Unknown User'}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {campaign.createdAt ? format(new Date(campaign.createdAt), 'MMM dd, yyyy') : 'N/A'}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      {campaign.campaignTypeKey === 'Standard' ? (
                        <Button variant="outline" size="sm" onClick={() => handleEditBooking(campaign)}>
                          <Edit className="h-4 w-4 mr-1" />
                          Edit Booking
                        </Button>
                      ) : null}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>



      {/* Edit Booking Modal */}
      <Dialog open={isEditBookingOpen} onOpenChange={setIsEditBookingOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Booking Configuration</DialogTitle>
            <DialogDescription>
              Update all aspects of this booking including campaign details, stores, and media formats
            </DialogDescription>
          </DialogHeader>
          
          {editingBooking && (
            <form className="space-y-6">
              {/* Campaign Basic Details */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold">Campaign Details</h3>
                <div className="grid gap-4 md:grid-cols-2">
                  <div>
                    <Label htmlFor="bookingName">Campaign Name</Label>
                    <Input 
                      id="bookingName" 
                      name="name" 
                      defaultValue={editingBooking.name}
                      required 
                    />
                  </div>
                  <div>
                    <Label htmlFor="bookingStatus">Status</Label>
                    <Select name="statusId" defaultValue={editingBooking.statusId?.toString() || "1"}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="z-[100]">
                        {!campaignStatuses || campaignStatuses.length === 0 ? (
                          <SelectItem value="loading" disabled>Loading statuses...</SelectItem>
                        ) : (
                          (campaignStatuses || []).map((status) => {
                            dbg.log('Rendering status:', status);
                            return (
                              <SelectItem key={status.id} value={status.id.toString()}>
                                <div className="flex items-center gap-2">
                                  <div 
                                    className="w-3 h-3 rounded-full" 
                                    style={{ backgroundColor: status.color || '#6b7280' }}
                                  />
                                  {status.name}
                                </div>
                              </SelectItem>
                            );
                          })
                        )}
                      </SelectContent>
                    </Select>
                    {/* Debug info */}
                    <div className="text-xs text-muted-foreground mt-1">
                      Statuses loaded: {campaignStatuses.length}
                    </div>
                  </div>
                </div>
                <div>
                  <Label htmlFor="bookingDescription">Description</Label>
                  <Input 
                    id="bookingDescription" 
                    name="description" 
                    defaultValue={editingBooking.description || ""}
                    placeholder="Campaign description" 
                  />
                </div>
                <div>
                  <Label htmlFor="bookingCycleId">Booking Cycle</Label>
                  <Select name="cycleId" defaultValue={editingBooking.cycleId?.toString() || "none"}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select a booking cycle (optional)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No cycle assigned</SelectItem>
                      {(allCycles || []).map((cycle: BookingCycle) => (
                        <SelectItem key={cycle.id} value={cycle.id.toString()}>
                          {cycle.name} - {format(new Date(cycle.startDate), "MMM dd")} to {format(new Date(cycle.endDate), "MMM dd, yyyy")}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Retailer Information */}
              {selectedRetailer && (
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold">Retailer</h3>
                  <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="text-sm font-medium text-blue-800">
                      {retailers.find(r => r.id === selectedRetailer)?.name || 'Unknown Retailer'}
                    </div>
                    <div className="text-xs text-blue-600 mt-1">
                      Only stores from this retailer are shown below
                    </div>
                  </div>
                </div>
              )}

              {/* Store Selection */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold">Store Selection</h3>
                <div className="max-h-48 overflow-y-auto border rounded-lg p-3">
                  <div className="grid grid-cols-1 gap-2">
                    {(stores || [])
                      .filter((store: Store) => selectedRetailer ? store.retailerId === selectedRetailer : true)
                      .map((store: Store) => (
                      <div key={store.id} className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          id={`store-${store.id}`}
                          checked={selectedStores.includes(store.id)}
                          onChange={() => {
                            if (selectedStores.includes(store.id)) {
                              setSelectedStores(prev => prev.filter(id => id !== store.id));
                            } else {
                              setSelectedStores(prev => [...prev, store.id]);
                            }
                          }}
                          className="rounded"
                        />
                        <Label htmlFor={`store-${store.id}`} className="flex-1">
                          <div className="font-medium">{store.name}</div>
                          <div className="text-sm text-muted-foreground">{store.address}</div>
                        </Label>
                      </div>
                    ))}
                  </div>
                  <div className="text-sm text-muted-foreground mt-2">
                    {selectedStores.length} of {(stores || []).filter((store: Store) => selectedRetailer ? store.retailerId === selectedRetailer : true).length} store(s) selected
                  </div>
                </div>
              </div>

              {/* Media Format Selection */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold">Quantity Per Store</h3>
                <div className="space-y-3">
                  {(mediaFormats || []).map((format: MediaFormat) => {
                    const selectedFormat = selectedMediaFormats.find(m => m.formatId === format.id);
                    return (
                      <div key={format.id} className="flex items-center space-x-4 p-3 border rounded-lg">
                        <div className="flex-1">
                          <div className="font-medium">{format.name}</div>
                          <div className="text-sm text-muted-foreground">{format.description}</div>
                          <div className="text-xs text-muted-foreground">
                            {format.dimensions} • {format.type}
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Label htmlFor={`quantity-${format.id}`}>Quantity:</Label>
                          <Input
                            id={`quantity-${format.id}`}
                            type="number"
                            min="0"
                            className="w-20"
                            value={selectedFormat?.quantity || 0}
                            onChange={(e) => {
                              const qty = parseInt(e.target.value) || 0;
                              if (qty > 0) {
                                setSelectedMediaFormats(prev => {
                                  const existing = prev.find(m => m.formatId === format.id);
                                  if (existing) {
                                    return prev.map(m => 
                                      m.formatId === format.id ? { ...m, quantity: qty } : m
                                    );
                                  } else {
                                    return [...prev, { formatId: format.id, quantity: qty }];
                                  }
                                });
                              } else {
                                setSelectedMediaFormats(prev => prev.filter(m => m.formatId !== format.id));
                              }
                            }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="text-sm text-muted-foreground">
                  {selectedMediaFormats.length} media format(s) configured
                </div>
              </div>

              <div className="flex justify-between pt-4">
                <Button 
                  type="button" 
                  variant="destructive"
                  onClick={async () => {
                    if (!editingBooking) return;
                    
                    const handleDeletion = async (confirmTwin = false) => {
                      try {
                        const url = confirmTwin ? 
                          `/api/campaigns/${editingBooking.id}?confirmTwin=true` : 
                          `/api/campaigns/${editingBooking.id}`;
                        
                        const result: any = await apiRequest(url, "DELETE");
                        
                        toast({
                          title: "Success",
                          description: result.message || "Campaign processed successfully",
                        });

                        setIsEditBookingOpen(false);
                        setEditingBooking(null);
                        setSelectedStores([]);
                        setSelectedMediaFormats([]);
                        
                        // Refresh campaigns
                        await queryClient.invalidateQueries({ queryKey: ['/api/campaigns'] });
                      } catch (error: any) {
                        
                        // Check if this is a twin campaign confirmation error
                        if (error.status === 409 && error.data?.requiresConfirmation) {
                          const twinName = error.data.twinCampaignName;
                          const confirmMessage = `This campaign has a linked removal campaign "${twinName}" that will also be deleted.\n\nAre you sure you want to delete both campaigns? This action cannot be undone.`;
                          
                          if (confirm(confirmMessage)) {
                            await handleDeletion(true);
                          }
                          return;
                        }
                        
                        toast({
                          title: "Error",
                          description: error.data?.message || error.message || "Failed to delete campaign",
                          variant: "destructive",
                        });
                      }
                    };
                    
                    // Check campaign status to determine deletion behavior
                    const isDraft = editingBooking.status === 'draft' || editingBooking.status === 'optioned';
                    const confirmMessage = isDraft 
                      ? `Are you sure you want to permanently delete the campaign "${editingBooking.name}"? This draft will be completely removed and cannot be recovered.`
                      : `Are you sure you want to archive the campaign "${editingBooking.name}"? The campaign will be archived to preserve historical data.`;
                    
                    if (confirm(confirmMessage)) {
                      await handleDeletion();
                    }
                  }}
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  {(editingBooking?.status === 'draft' || editingBooking?.status === 'optioned' || editingBooking?.status === 'archived') ? 'Delete Campaign' : 'Archive Campaign'}
                </Button>
                <div className="flex gap-2">
                  <Button type="button" variant="outline" onClick={() => setIsEditBookingOpen(false)}>
                    Cancel
                  </Button>
                  <Button 
                    type="submit"
                    onClick={async (e) => {
                      e.preventDefault();
                    if (!editingBooking) return;

                    try {
                      const formData = new FormData(e.currentTarget.form!);
                      const campaignData = {
                        name: formData.get('name') as string,
                        description: formData.get('description') as string,
                        statusId: parseInt(formData.get('statusId') as string),
                        cycleId: formData.get('cycleId') === 'none' ? null : parseInt(formData.get('cycleId') as string)
                      };

                      console.log('Updating campaign with data:', campaignData);
                      console.log('Campaign ID:', editingBooking.id);

                      // Update campaign
                      await apiRequest(`/api/campaigns/${editingBooking.id}`, "PATCH", campaignData);

                      // Clear existing relationships
                      await apiRequest(`/api/campaigns/${editingBooking.id}/relationships`, "DELETE");

                      // Add stores
                      if (selectedStores.length > 0) {
                        await apiRequest(`/api/campaigns/${editingBooking.id}/stores`, "POST", {
                          stores: selectedStores.map((storeId: number) => ({ type: 'store', storeId }))
                        });
                      }

                      // Add media formats
                      if (selectedMediaFormats.length > 0) {
                        await apiRequest(`/api/campaigns/${editingBooking.id}/media`, "POST", {
                          media: selectedMediaFormats.map((m: any) => ({ 
                            mediaFormatId: m.formatId, 
                            quantity: m.quantity 
                          }))
                        });
                      }

                      toast({
                        title: "Success",
                        description: "Booking updated successfully",
                      });

                      setIsEditBookingOpen(false);
                      setEditingBooking(null);
                      setSelectedStores([]);
                      setSelectedMediaFormats([]);
                      
                      // Refresh campaigns
                      await queryClient.invalidateQueries({ queryKey: ['/api/campaigns'] });
                    } catch (error) {
                      toast({
                        title: "Error",
                        description: "Failed to update booking",
                        variant: "destructive",
                      });
                    }
                  }}
                >
                  Update Booking
                </Button>
                </div>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
